import t from 'react-translate';
import { useContext, useEffect, useRef, useState } from 'react';
import { AngularContext } from 'react-app';
import prodPathReplace from 'shared/prod-path-rewrite';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { css } from '@emotion/react';
import { gray6, white } from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  extraLargeSpacing,
  halfSpacing,
  largeSpacing,
  standardSpacing,
  topHeaderHeight,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import InlineVideoPreview from 'athena/components/inline-video-preview';
import NvUploadedFile from 'shared/components/nv-uploaded-file';
import { UploadedFile } from 'redux/schemas/models/file-uploads';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import { config } from '@config/pendo.config.json';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';
import { useMediaQuery } from 'react-responsive';
import { screenMdMin, screenSmMin } from 'styles/global_defaults/media-queries';

const COMPACT_TITLE_MAX_LINES = 1;
const DEFAULT_TITLE_MAX_LINES = 2;
const doubleModalZIndex = 7050;

type GuideModalProps = {
  mentoringProgramGuide: MentoringProgramGuide;
};


const marginMap = {
  left: {
    mobile: `${standardSpacing}px`,
    tablet: `${largeSpacing}px`,
    desktop: '82px',
  },
  right: {
    mobile: `${standardSpacing}px`,
    tablet: '32px',
    desktop: '108px',
  },
};

function getGuideContentBodyMargin(side: 'left' | 'right', isMobile: boolean, isTablet: boolean) {
  if (isMobile) return marginMap[side].mobile;
  if (isTablet) return marginMap[side].tablet;
  return marginMap[side].desktop;
}

const backdropStyles = css`
  background: transparent;
  z-index: 1;
`

const styles = (
  src: string,
  isScrolled: boolean,
  headerHeight: number,
  isTablet: boolean,
  isMobile: boolean
) => css`
  z-index: 2;

  .guide-header {
    background-color: ${white};
    transition: height 0.3s ease, background-size 0.3s ease;
    position: sticky;

    ${isScrolled
      ? css`
          height: 80px;
          align-items: center;
          justify-content: left;
          border-bottom: 1px solid ${gray6};
          background: linear-gradient(
            90deg,
            rgba(235, 179, 129, 0.1) 0%,
            rgba(147, 218, 225, 0.1) 100%
          );
        `
      : css`
          background: url('${src}') no-repeat top center;
          height: 100%;
          justify-content: center;
          background-size: contain;
        `}
  }

  .guide-title-wrapper {
    height: fit-content;
  }

  .guide-title {
    width: unset;
    max-height: fit-content;
    line-height: unset;
    ${isScrolled
      ? css`
          margin: 0 65px 0 ${doubleSpacing}px;
        `
      : css`
          text-align: center;
          margin: ${tripleSpacing}px ${extraLargeSpacing}px ${largeSpacing}px;
        `}
    transition: all 0.3s ease;
  }

  .guide-content {
    padding-top: ${halfSpacing}px;
    top: ${isScrolled ? '80px' : `${headerHeight}px`};
    height: calc(100% - ${isScrolled ? topHeaderHeight : 0}px - ${headerHeight}px);
    overflow-y: auto;
    padding-bottom: ${doubleSpacing}px;
  }

  .guide-content-body {
    box-shadow: ${isScrolled ? 'none' : '0px 4px 10px 0px rgba(0, 0, 0, 0.1)'};
    gap: ${largeSpacing}px;
    margin-left: ${getGuideContentBodyMargin('left', isMobile, isTablet)};
    margin-right: ${getGuideContentBodyMargin('right', isMobile, isTablet)};
    background: ${white};
    padding: ${doubleSpacing}px;
    border-radius: ${standardSpacing}px;
    transition: all 0.3s ease;
  }
`;

const GuideModal = ({ mentoringProgramGuide }: GuideModalProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const src = prodPathReplace('images/program-guide-bg.svg');
  const translationBase = t.MENTORING_PROGRAMS.GUIDES;
  const dataQATag =
    config.pendo.athena.mentorshipProgram.participantHome.guidance.modal;
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const isTablet = useMediaQuery({ query: `(max-width: ${screenMdMin}px)` });
  const isMobile = useMediaQuery({ query: `(max-width: ${screenSmMin}px)` });

  const resetGuideIdParam = () => {
    const params = { guideId: undefined };
    $state.go($state.current, params, { notify: false });
  };

  const { title, content, video, attachments } = mentoringProgramGuide || {};

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const bodyEl = document.querySelector('.guide-content');
    if (!bodyEl) return;
    const handleScroll = () => {
      setIsScrolled((bodyEl as HTMLElement).scrollTop > 20);
    };
    bodyEl.addEventListener('scroll', handleScroll);
    return () => {
      bodyEl.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const observer = new ResizeObserver(() => {
      setHeaderHeight(el.offsetHeight);
    });
    observer.observe(el);
    setHeaderHeight(el.offsetHeight);

    return () => observer.disconnect();
  }, []);

  return (
    <NvModal
      show
      doubleModal
      fullHeight={false}
      type={ModalType.NO_DIALOG}
      closeDataQa={dataQATag.close}
      onClose={resetGuideIdParam}
      body={
        <div className='position-relative w-100 h-100'>
          {/* Transparent click-catcher overlay for backdrop clicks */}
          <div
            css={backdropStyles}
            className='position-fixed w-100 h-100 top-0 left-0'
            onClick={resetGuideIdParam}
          />
          {/* Modal content, with higher z-index */}
          <div
            css={styles(src, isScrolled, headerHeight, isTablet, isMobile)}
            className='modal-page position-relative'
          >
            <div className='d-flex guide-header'>
              <div ref={headerRef} className='guide-title-wrapper'>
                <NvTruncatedTextWithTooltip
                  zIndex={doubleModalZIndex}
                  className={`align-items-start ${
                    isScrolled ? 'text-xl' : 'header-xl'
                  } font-weight-light guide-title`}
                  linesCount={
                    isScrolled
                      ? COMPACT_TITLE_MAX_LINES
                      : DEFAULT_TITLE_MAX_LINES
                  }
                  text={title}
                />
              </div>
            </div>
            <div className='position-absolute guide-content w-100'>
              <div className='d-flex flex-column guide-content-body'>
                <div
                  className='text-large-body mb-0'
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                />
                {video && (
                  <InlineVideoPreview
                    videoSrc={video.url}
                    title={video.fileName}
                    thumbnailSrc={video.thumbnailUrl}
                    dataQa={dataQATag.videoPlay}
                    dataQaId={`${dataQATag.videoPlay}-${video.fileName}`}
                    ariaLabel={translationBase.ARIA_LABEL.PLAY_VIDEO(
                      video.fileName
                    )}
                  />
                )}
                {attachments?.length ? (
                  <div>
                    <div className='text-small semi-bold mb-2'>
                      {translationBase.RELATED_MATERIALS()}
                    </div>
                    <ul className='list-unstyled'>
                      {attachments.map((attachment) => {
                        const {
                          fileName: name,
                          fileSize: size,
                          contentType: type,
                          url,
                        } = attachment;
                        const uploadedFile: UploadedFile = {
                          uniqueId: `attachment-${name}`,
                          name,
                          size,
                          type,
                          url,
                        };
                        return (
                          <li key={name} className='mb-1'>
                            <NvUploadedFile
                              file={uploadedFile}
                              displayTopOptions={false}
                              displayInline={false}
                              openModalOnClick={false}
                              downloadOnClick
                              downloadDataQa={dataQATag.attachmentDownload}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};
export default GuideModal;
